import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { fetchArchiveQuestions } from 'actions/archive';
import { NonIdealState } from '@blueprintjs/core';
import {
  Container,
  Table,
  TableHeader,
  TableData,
  PaddingWrapper,
} from 'components/styled-components';
import {
  Wrapper,
  TitleWrapper,
  Title,
  TableRow,
  QuestionDetails,
  DetailItemWrapper,
  DetailItemTitle,
  DetailItemValue,
  Status,
  StatusLabel,
  StatusValue,
} from 'containers/Manage-Questionnaire/styled-components';
import map from 'lodash/map';
import { findValue } from 'utils/functions';
import Pagination from 'components/pagination';
import colors from 'utils/colors';
import shortid from 'shortid';

const QuestionRow = ({ question, categories, questionTypes }) => (
  <Container background={colors.whiteGray}>
    <Wrapper>
      <TitleWrapper>
        <Title>{question.question}</Title>
        <Status>
          <StatusLabel>QUESTION ID:</StatusLabel>{' '}
          <StatusValue>{question.questionID}</StatusValue>
        </Status>
      </TitleWrapper>
      <AnswerTable answers={question.answers} />
      <DetailsRow
        question={question}
        categories={categories}
        questionTypes={questionTypes}
      />
    </Wrapper>
  </Container>
);

const AnswerTable = ({ answers }) => (
  <Table>
    <tbody>
      <TableRow>
        <TableHeader>ID</TableHeader>
        <TableHeader>Answer</TableHeader>
        <TableHeader>Score</TableHeader>
        <TableHeader>Auto Fail</TableHeader>
      </TableRow>
      {map(answers, (answer, i) => {
        if (answer.answer === 'OPT_OUT') return;
        else {
          return (
            <AnswerRow
              key={`${answer.answerID}-${shortid.generate()}`}
              answer={answer}
            />
          );
        }
      })}
      <TableRow />
    </tbody>
  </Table>
);

const AnswerRow = ({ answer }) => (
  <TableRow key={answer.answerID}>
    <TableData>{answer.answerID}</TableData>
    <TableData>{answer.answer}</TableData>
    <TableData>{answer.score}</TableData>
    <TableData>{answer.autoFail ? 'Yes' : 'No'}</TableData>
  </TableRow>
);

const DetailsRow = ({ question, categories, questionTypes }) => (
  <QuestionDetails style={{ borderRadius: '0 0 3px 3px' }}>
    <DetailItemWrapper>
      <DetailItemTitle>Question Type</DetailItemTitle>
      <DetailItemValue>
        {findValue(
          questionTypes,
          'typeID',
          question.questionTypeID,
          'typeName'
        )}
      </DetailItemValue>
    </DetailItemWrapper>
    <DetailItemWrapper>
      <DetailItemTitle>Control Number</DetailItemTitle>
      <DetailItemValue>{question.controlID}</DetailItemValue>
    </DetailItemWrapper>
    <DetailItemWrapper>
      <DetailItemTitle>Display Category</DetailItemTitle>
      <DetailItemValue>
        {findValue(
          categories,
          'categoryID',
          question.displayCategoryID,
          'categoryName'
        )}
      </DetailItemValue>
    </DetailItemWrapper>
    <DetailItemWrapper>
      <DetailItemTitle>Weight</DetailItemTitle>
      <DetailItemValue>{question.weight}</DetailItemValue>
    </DetailItemWrapper>
    <DetailItemWrapper>
      <DetailItemTitle>Default</DetailItemTitle>
      <DetailItemValue>
        {question.defaultQuestion ? 'Yes' : 'No'}
      </DetailItemValue>
    </DetailItemWrapper>
  </QuestionDetails>
);

const PaginationRow = ({
  perPage,
  currentPage,
  numRecords,
  onPageClick,
  handlePerPageChange,
}) => (
  <Pagination
    perPage={perPage}
    currentPage={currentPage}
    numRecords={numRecords}
    onPageClick={onPageClick}
    handlePerPageChange={handlePerPageChange}
  />
);

const ArchiveTableBase = ({
  questions,
  categories,
  questionTypes,
  numRecords,
  fetchArchiveQuestions,
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [perPage, setPerPage] = useState(10);

  useEffect(() => {
    fetchArchiveQuestions({
      start: currentPage * perPage,
      limit: perPage,
    });
  }, [currentPage, perPage]);

  const handlePageClick = currentPage => {
    setCurrentPage(currentPage);
  };

  const handlePerPageChange = perPage => {
    setPerPage(perPage.value);
    setCurrentPage(0);
  };

  if (questions.length === 0) {
    return (
      <NonIdealState
        icon="box"
        title="No questions found."
        description="There are no questions currently archived."
      />
    );
  } else {
    return (
      <React.Fragment>
        <PaddingWrapper />
        {questions.map(question => (
          <QuestionRow
            key={shortid.generate()}
            question={question}
            categories={categories}
            questionTypes={questionTypes}
          />
        ))}
        <PaginationRow
          perPage={perPage}
          currentPage={currentPage}
          numRecords={numRecords}
          onPageClick={handlePageClick}
          handlePerPageChange={handlePerPageChange}
        />
      </React.Fragment>
    );
  }
};

const mapStateToProps = state => ({
  questions: state.archive.archived,
  numRecords: state.archive.totalCount,
  isFetching: state.archive.isFetching,
});

const ArchiveTable = connect(
  mapStateToProps,
  {
    fetchArchiveQuestions,
  }
)(ArchiveTableBase);

export default ArchiveTable;
